













































































































































import axios from 'axios';
import Swal from 'sweetalert2';
import { Component, Prop, Vue, Provide } from 'vue-property-decorator';
import { Validator } from 'vee-validate';

import { Product } from '@/models/catalog/Product';
import { GetProductImages } from '@/services/ImageService';
import { UserRole } from '@/models/account/UserRole';
import { Permission, SEE_INTERNAL_NOTES, SEE_ORIGINAL_PRICE } from '@/models/account/Permission';
import { padleft } from '@/services/StringService';

import ProductImageCarousel from '@/components/App/ProductImageCarousel/ProductImageCarousel.vue';
import ProductSpecificationTable from '@/components/App/ProductSpecificationTable/ProductSpecificationTable.vue';
import ProductPriceTable from '@/components/App/ProductPriceTable/ProductPriceTable.vue';
import ProductOption from '@/components/App/ProductOption/ProductOption.vue';
import ProductPrice from '@/components/App/ProductPrice/ProductPrice.vue';
import ProductItem from '@/components/App/Catalog/ProductItem.vue';

@Component({
    components: {
        'app-product-image-carousel': ProductImageCarousel,
        'app-product-specification-table': ProductSpecificationTable,
        'app-product-price-table': ProductPriceTable,
        'app-product-option': ProductOption,
        'app-product-price': ProductPrice,
        'app-related-product': ProductItem,
    },
})
export default class ProductPage extends Vue {
    @Provide('validator')
    public $validator: Validator = this.$validator;

    public slug: string = '';

    public product: Product | null = null;

    public relatedProducts: Product[] | null = null;

    public price: number = 0;

    public pricePerUnit: number = 0;

    public inPrice: number = 0;

    public inPricePerUnit: number = 0;

    public file!: File|null;

    public fileCount: number = 0;

    get authenticated (): boolean {
        return this.$store.getters['auth/check'];
    }

    get canSeeNotes (): boolean {
        return this.$store.getters['auth/hasPermission'](SEE_INTERNAL_NOTES);
    }

    get productImages (): string[] {
        if (!this.product) {
            return [];
        }
        
        return GetProductImages(this.product);
    }

    get hasProductDescription (): boolean {
        if (this.product && this.product.detailed_description !== null) {
            return true;
        }

        return false;
    }

    get hasPrintTemplate (): boolean {
        if (!this.product || !this.product.print_templates) {
            return false;
        }

        return this.product.print_templates.length > 0;
    }

    get hasProductSpecification(): boolean {
        if (!this.product || !this.product.specifications) {
            return false;
        }

        return this.product.specifications.length > 0;
    }

    get hasRelatedProducts(): boolean {
        return this.relatedProducts !== null
            && this.relatedProducts.length > 0;
    }

    get productAlert(): string {
        if (!this.hasRelatedProducts || !this.relatedProducts) {
            return '';
        }

        if (this.relatedProducts.length > 1) {
            return 'Denna produkt har flera relaterade produkter, du hittar dem nedan.';
        }
        
        return 'Denna produkt har en relaterad produkt, du hittar den nedan.';
    }

    get articleNumber (): string|void {
        if (this.product && this.product.external_id) {
            return this.product.external_id;
        } else if (this.product) {
            return `B-${padleft(this.product.id)}`;
        }
    }

    public async onAddToCartClicked () {
        const valid = await this.$validator.validateAll();

        if (!valid) {
            return;
        }

        axios.post('/cart/items', this.getFormData())
            .then(() => {
                Swal.fire({
                    title: 'Du är grym!',
                    text: 'Produkten finns nu i din varukorg.',
                    type: 'success',
                    showCloseButton: false,
                    showCancelButton: false,
                    timer: 3500,
                });

                this.$store.dispatch('shared/fetch_notifications');
            })
            .catch((error) => {
                this.$store.dispatch('shared/error', error.response);
            });
    }

    public async onValueChanged (isInitialCalculation: boolean) {
        const formData = this.getFormData();

        if (isInitialCalculation && (formData.has('height') || formData.has('width'))) {
            return;
        }
        
        const valid = await this.$validator.validateAll();

        if (!valid) {
            return;
        }
            
        axios.post('/catalog/calculate', formData)
            .then((response) => {
                this.price = response.data.price;
                this.pricePerUnit = response.data.pricePerUnit;
                this.inPrice = response.data.inPrice;
                this.inPricePerUnit = response.data.inPricePerUnit;
            })
            .catch((error) => {
                this.$store.dispatch('shared/error', error.response);
            });
    }

    public onFileChanged (event: any) {
        this.file = event.target.files[0];
        this.fileCount = 1;
    }

    public fetchProduct () {
        return axios.get(`/catalog/product/${this.slug}`)
            .then((response) => {
                this.product = response.data;
            })
            .catch((error) => {
                this.$store.dispatch('shared/error', error.response);
            });
    }

    public fetchRelatedProducts () {
        return axios.get(`/catalog/product/${this.slug}/related`)
            .then((response) => {
                this.relatedProducts = response.data;
            })
            .catch((error) => {
                this.$store.dispatch('shared/error', error.response);
            })
    }

    public getFormData () {
        const form = this.$refs.form as HTMLFormElement;
        const formData = new FormData(form);

        if (this.product != null && this.product.id !== null) {
            formData.append('pid', String(this.product.id));
        }

        if (this.file) {
            formData.append('template', this.file, this.file.name);
        }
        
        return formData;
    }

    public created () {
        this.slug = this.$route.params.product;
        
        this.fetchRelatedProducts();
        
        this.fetchProduct().then(() => {
            this.onValueChanged(true)
        });
    }
}
